import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { trackEvent, trackGoal } from './Matomo';
import { QUOTE_SERVICE_OPTIONS } from '../data/quoteFaq';
import './QuoteForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    budget_range: '',
    timeline: '',
    description: '',
    _honeypot: '', // Champ anti-bot (invisible)
    _timestamp: Date.now() // Timestamp de chargement du formulaire
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API_URL}/quotes`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_type: '',
        budget_range: '',
        timeline: '',
        description: '',
        _honeypot: '',
        _timestamp: Date.now()
      });
      
      // Tracker l'événement dans Matomo
      trackEvent('Quote', 'Submit', formData.service_type);
      trackGoal(2); // Objectif 2 = Devis demandé
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
      trackEvent('Quote', 'Error', 'Quote Form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="quote-form-section" aria-labelledby="quote-form-title">
      <div className="container">
        <div className="quote-form-wrapper">
          <h2 id="quote-form-title" className="quote-form-heading">
            Décrivez votre projet
          </h2>
            {submitStatus === 'success' && (
              <div className="alert alert-success">
                <h3>Demande de devis envoyée avec succès !</h3>
                <p>Notre équipe vous contactera dans les plus brefs délais pour discuter de votre projet.</p>
                <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
              </div>
            )}

            {submitStatus !== 'success' && (
              <form className="quote-form" onSubmit={handleSubmit}>
                {submitStatus === 'error' && (
                  <div className="alert alert-error">
                    Une erreur est survenue. Veuillez réessayer.
                  </div>
                )}

                <div className="form-section">
                  <h3 className="form-section-title">Informations personnelles</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Nom complet *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Téléphone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-input"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Entreprise</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="form-input"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="form-section-title">Détails du projet</h3>
                  <div className="form-group">
                    <label htmlFor="service_type">Type de projet *</label>
                    <select
                      id="service_type"
                      name="service_type"
                      className="form-input"
                      value={formData.service_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Sélectionnez un type de projet</option>
                      {QUOTE_SERVICE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="budget_range">Budget estimé</label>
                      <select
                        id="budget_range"
                        name="budget_range"
                        className="form-input"
                        value={formData.budget_range}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionnez un budget</option>
                        <option value="moins_5k">Moins de 5 000 €</option>
                        <option value="5k_10k">5 000 € - 10 000 €</option>
                        <option value="10k_25k">10 000 € - 25 000 €</option>
                        <option value="25k_50k">25 000 € - 50 000 €</option>
                        <option value="plus_50k">Plus de 50 000 €</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="timeline">Délai souhaité</label>
                      <select
                        id="timeline"
                        name="timeline"
                        className="form-input"
                        value={formData.timeline}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionnez un délai</option>
                        <option value="urgent">Urgent (moins d'1 mois)</option>
                        <option value="1_3_mois">1 à 3 mois</option>
                        <option value="3_6_mois">3 à 6 mois</option>
                        <option value="plus_6_mois">Plus de 6 mois</option>
                        <option value="flexible">Flexible</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description du projet *</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-input"
                      rows="6"
                      placeholder="Décrivez votre contexte, vos usages, volumes estimés et contraintes d’intégration..."
                      value={formData.description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                </div>

                {/* Champ honeypot anti-bot (caché avec CSS) */}
                <input
                  type="text"
                  name="_honeypot"
                  value={formData._honeypot}
                  onChange={handleChange}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="form-actions">
                  <Link to="/" className="btn btn-secondary">
                    Annuler
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
  );
}

export default QuoteForm;
